import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

interface Player {
  id: string;
  name: string;
  jerseyNumber?: number;
  position?: string;
}

interface MatchEvent {
  type: 'goal' | 'assist' | 'yellow_card' | 'red_card';
  playerId: string;
  teamId: string;
  minute?: number;
  notes?: string;
}

interface Match {
  id: string;
  homeTeamId: string;
  awayTeamId: string;
  scheduledDate?: string;
  scheduledTime?: string;
  location?: string;
  homeTeam: { id: string; name: string } | null;
  awayTeam: { id: string; name: string } | null;
  homeTeamMembers: Player[];
  awayTeamMembers: Player[];
  events: MatchEvent[];
}

const MatchCompletion: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [match, setMatch] = useState<Match | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [homeScore, setHomeScore] = useState<number>(0);
  const [awayScore, setAwayScore] = useState<number>(0);
  const [events, setEvents] = useState<MatchEvent[]>([]);
  const [notes, setNotes] = useState('');
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvent, setNewEvent] = useState<Partial<MatchEvent>>({
    type: 'goal',
    playerId: '',
    teamId: '',
    minute: undefined
  });

  useEffect(() => {
    if (user?.profileType !== 'referee') {
      navigate('/dashboard');
      return;
    }

    fetchMatch();
  }, [matchId, user, navigate]);

  const fetchMatch = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/referees/matches/${matchId}`);
      setMatch(response.data);
      setHomeScore(response.data.homeScore || 0);
      setAwayScore(response.data.awayScore || 0);
      setEvents(response.data.events || []);
      setNotes(response.data.notes || '');
    } catch (error) {
      console.error('Error fetching match:', error);
      alert('Failed to load match details');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = () => {
    if (!newEvent.type || !newEvent.playerId || !newEvent.teamId) {
      alert('Please fill in all required fields for the event');
      return;
    }

    setEvents([...events, newEvent as MatchEvent]);
    setNewEvent({
      type: 'goal',
      playerId: '',
      teamId: '',
      minute: undefined
    });
    setShowAddEvent(false);
  };

  const handleRemoveEvent = (index: number) => {
    setEvents(events.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (homeScore < 0 || awayScore < 0) {
      alert('Scores cannot be negative');
      return;
    }

    // Validate that goal count matches events
    const goalCount = events.filter(e => e.type === 'goal').length;
    if (goalCount !== homeScore + awayScore) {
      if (!window.confirm(`You have ${goalCount} goal events but the score totals ${homeScore + awayScore}. Continue anyway?`)) {
        return;
      }
    }

    setSubmitting(true);
    try {
      await api.post(`/referees/matches/${matchId}/complete`, {
        homeScore,
        awayScore,
        events,
        notes
      });
      alert('Match completed successfully!');
      navigate('/referees/dashboard');
    } catch (error: any) {
      console.error('Error completing match:', error);
      alert(error.response?.data?.error || 'Failed to complete match');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!match) {
    return <div className="container">Match not found</div>;
  }

  const homeTeamPlayers = match.homeTeamMembers || [];
  const awayTeamPlayers = match.awayTeamMembers || [];

  return (
    <div className="container">
      <h1>Complete Match</h1>
      
      <div className="card" style={{ marginBottom: '20px' }}>
        <h2>
          {match.homeTeam?.name || 'TBD'} vs {match.awayTeam?.name || 'TBD'}
        </h2>
        <p><strong>Date:</strong> {new Date(match.scheduledDate || '').toLocaleDateString()}</p>
        <p><strong>Time:</strong> {match.scheduledTime}</p>
        <p><strong>Location:</strong> {match.location}</p>
      </div>

      <form onSubmit={handleSubmit} className="card">
        <h2>Final Score</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div className="form-group">
            <label>{match.homeTeam?.name || 'Home Team'} Score</label>
            <input
              type="number"
              min="0"
              value={homeScore}
              onChange={(e) => setHomeScore(parseInt(e.target.value) || 0)}
              className="form-control"
              required
            />
          </div>
          <div className="form-group">
            <label>{match.awayTeam?.name || 'Away Team'} Score</label>
            <input
              type="number"
              min="0"
              value={awayScore}
              onChange={(e) => setAwayScore(parseInt(e.target.value) || 0)}
              className="form-control"
              required
            />
          </div>
        </div>

        <h2>Match Events</h2>
        <div style={{ marginBottom: '20px' }}>
          {events.length === 0 ? (
            <p style={{ color: '#757575' }}>No events added yet</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Player</th>
                  <th>Team</th>
                  <th>Minute</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, index) => {
                  const player = [...homeTeamPlayers, ...awayTeamPlayers].find(p => p.id === event.playerId);
                  const team = event.teamId === match.homeTeamId ? match.homeTeam : match.awayTeam;
                  return (
                    <tr key={index}>
                      <td>
                        {event.type === 'goal' && '⚽ Goal'}
                        {event.type === 'assist' && '🎯 Assist'}
                        {event.type === 'yellow_card' && '🟨 Yellow Card'}
                        {event.type === 'red_card' && '🟥 Red Card'}
                      </td>
                      <td>{player?.name || 'Unknown'}</td>
                      <td>{team?.name || 'Unknown'}</td>
                      <td>{event.minute || 'N/A'}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveEvent(index)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          
          {!showAddEvent ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowAddEvent(true)}
            >
              Add Event
            </button>
          ) : (
            <div className="card" style={{ marginTop: '15px', padding: '15px' }}>
              <h3>Add Event</h3>
              <div className="form-group">
                <label>Event Type</label>
                <select
                  value={newEvent.type}
                  onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value as any })}
                  className="form-control"
                >
                  <option value="goal">⚽ Goal</option>
                  <option value="assist">🎯 Assist</option>
                  <option value="yellow_card">🟨 Yellow Card</option>
                  <option value="red_card">🟥 Red Card</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Team</label>
                <select
                  value={newEvent.teamId || ''}
                  onChange={(e) => {
                    setNewEvent({ ...newEvent, teamId: e.target.value, playerId: '' });
                  }}
                  className="form-control"
                >
                  <option value="">Select Team</option>
                  <option value={match.homeTeamId}>{match.homeTeam?.name}</option>
                  <option value={match.awayTeamId}>{match.awayTeam?.name}</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Player</label>
                <select
                  value={newEvent.playerId || ''}
                  onChange={(e) => setNewEvent({ ...newEvent, playerId: e.target.value })}
                  className="form-control"
                  disabled={!newEvent.teamId}
                >
                  <option value="">Select Player</option>
                  {(newEvent.teamId === match.homeTeamId ? homeTeamPlayers : awayTeamPlayers).map(player => (
                    <option key={player.id} value={player.id}>
                      #{player.jerseyNumber} {player.name} {player.position ? `(${player.position})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Minute (optional)</label>
                <input
                  type="number"
                  min="0"
                  max="120"
                  value={newEvent.minute || ''}
                  onChange={(e) => setNewEvent({ ...newEvent, minute: parseInt(e.target.value) || undefined })}
                  className="form-control"
                />
              </div>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddEvent}
                >
                  Add Event
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddEvent(false);
                    setNewEvent({ type: 'goal', playerId: '', teamId: '', minute: undefined });
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="form-control"
            rows={3}
            placeholder="Additional notes about the match..."
          />
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Completing...' : 'Complete Match'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/referees/dashboard')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default MatchCompletion;

